import { useEffect, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { MESI } from "@/lib/format";
import { SelettoreData } from "@/components/SelettoreData";

export const MESE_CORRENTE = new Date().getMonth() + 1;
export const ANNO_CORRENTE = new Date().getFullYear();

export function useFiltersMeta() {
  const [meta, setMeta] = useState(null);
  useEffect(() => { api.get("/dashboard/filters").then((r) => setMeta(r.data)).catch(() => setMeta({})); }, []);
  return meta;
}

const cls = "w-full sm:w-auto border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-colors duration-200";

export function FilterBar({ meta, value, onChange, fields = ["anno", "mese", "commerciale", "settore", "prodotto", "macro"], extra, dealers = [] }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value || "" });
  const [apertoMobile, setApertoMobile] = useState(false);
  if (!meta) return null;
  const attivi = Object.entries(value).filter(([k, v]) => v && k !== "anno").length;
  return (
    <>
    <button type="button" data-testid="filtri-toggle-mobile" onClick={() => setApertoMobile((v) => !v)}
      className={`sm:hidden mb-3 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm
        transition-colors duration-200 ${apertoMobile || attivi
          ? "border-[#0018A8] text-[#0018A8] bg-white" : "border-slate-200 text-slate-500 bg-white"}`}>
      <SlidersHorizontal size={16} />
      Filtri{attivi ? ` (${attivi})` : ""}
    </button>
    <div className={`${apertoMobile ? "grid" : "hidden"} grid-cols-2 gap-2 mb-4
      sm:flex sm:flex-wrap sm:items-center sm:gap-2`} data-testid="filter-bar">
      {fields.includes("anno") && (
        <select className={cls} value={value.anno || ""} onChange={set("anno")} data-testid="filter-anno">
          {(meta.anni || []).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      )}
      {fields.includes("mese") && (
        <select className={cls} value={value.mese || ""} data-testid="filter-mese"
          onChange={(e) => onChange({ ...value, mese: e.target.value || "", dal: "", al: "" })}>
          <option value="">Tutti i mesi</option>
          {MESI.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
      )}
      {fields.includes("intervallo") && (
        <div className="flex items-center gap-1 col-span-2" data-testid="filter-intervallo">
          <span className="text-xs text-slate-500">dal</span>
          <SelettoreData value={value.dal || ""} testId="filter-dal" compatto
            onChange={(v) => onChange({ ...value, dal: v, al: value.al && value.al < v ? v : value.al || "", mese: "" })} />
          <span className="text-xs text-slate-500">al</span>
          <SelettoreData value={value.al || ""} testId="filter-al" compatto
            onChange={(v) => onChange({ ...value, al: v, dal: value.dal && value.dal > v ? v : value.dal || "", mese: "" })} />
          {(value.dal || value.al) && (
            <button type="button" data-testid="filter-intervallo-reset" title="Togli intervallo"
              onClick={() => onChange({ ...value, dal: "", al: "" })}
              className="text-xs text-slate-400 hover:text-[#C8102E] px-1">✕</button>
          )}
        </div>
      )}
      {fields.includes("commerciale") && (
        <select className={cls} value={value.commerciale || ""} onChange={set("commerciale")} data-testid="filter-commerciale">
          <option value="">Tutti i commerciali</option>
          {(meta.commerciali || []).map((c) => <option key={c.slug} value={c.slug}>{c.nome}</option>)}
        </select>
      )}
      {fields.includes("macro") && (
        <select className={cls} value={value.macro || ""} onChange={set("macro")} data-testid="filter-macro">
          <option value="">AUTO / NON AUTO</option>
          <option value="AUTO">AUTO</option>
          <option value="NON AUTO">NON AUTO</option>
        </select>
      )}
      {fields.includes("settore") && (
        <select className={cls} value={value.settore || ""} onChange={set("settore")} data-testid="filter-settore">
          <option value="">Tutti i settori</option>
          {(meta.settori || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      )}
      {fields.includes("dealer") && (
        <>
          <SelettoreDealer value={value.dealer || ""} dealers={dealers}
            filtriBase={{ commerciale: value.commerciale, settore: value.settore }}
            annoDefault={value.anno}
            onChange={(v) => onChange({ ...value, dealer: v })} />
          <SelettoreDealer modo="escludi" value={value.dealer_escluso || ""} dealers={dealers}
            filtriBase={{ commerciale: value.commerciale, settore: value.settore }}
            annoDefault={value.anno}
            onChange={(v) => onChange({ ...value, dealer_escluso: v })} />
        </>
      )}
      {fields.includes("prodotto") && (
        <select className={cls} value={value.prodotto || ""} onChange={set("prodotto")} data-testid="filter-prodotto">
          <option value="">Tutti i prodotti</option>
          {(meta.prodotti || []).map((p) => (
            <option key={p.codice} value={p.codice}>{p.label}</option>
          ))}
        </select>
      )}
      {fields.includes("canale") && (
        <select className={cls} value={value.canale || ""} onChange={set("canale")} data-testid="filter-canale">
          <option value="">Tutti i canali</option>
          {(meta.canali || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      )}
      {fields.includes("stato") && (
        <select className={cls} value={value.stato || ""} onChange={set("stato")} data-testid="filter-stato">
          <option value="">Tutti gli stati</option>
          {(meta.stati || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      )}
      {fields.includes("gestione") && (
        <select className={cls} value={value.gestione || ""} onChange={set("gestione")} data-testid="filter-gestione">
          <option value="">Tutte le gestioni</option>
          {(meta.gestioni || []).map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
      )}
      {extra}
    </div>
    </>
  );
}

/** Selettore multiplo di convenzionati: chip rimovibili, ricerca e scorciatoie Top N. */
export function SelettoreDealer({ value, dealers = [], onChange, filtriBase = {}, annoDefault,
  modo = "includi" }) {
  const escludi = modo === "escludi";
  const tid = escludi ? "dealer-escluso" : "dealer";
  const [testo, setTesto] = useState("");
  const [apri, setApri] = useState(false);
  const [top, setTop] = useState({ n: 10, anno: (Number(annoDefault) || ANNO_CORRENTE) - 1,
    metrica: "importo_caricato" });
  const codici = value ? value.split(",").filter(Boolean) : [];
  const [nomi, setNomi] = useState({});
  const nome = (c) => nomi[c] || dealers.find((d) => d.codice === c)?.ragione_sociale || c;
  const q = testo.trim().toLowerCase();
  const suggeriti = (q
    ? dealers.filter((d) => (d.ragione_sociale || "").toLowerCase().includes(q))
    : dealers).filter((d) => !codici.includes(d.codice)).slice(0, 12);

  const aggiungi = (c) => { onChange([...codici, c].join(",")); setTesto(""); };
  const rimuovi = (c) => onChange(codici.filter((x) => x !== c).join(","));

  const caricaTop = () => {
    const p = new URLSearchParams({ anno: top.anno, metrica: top.metrica, n: top.n });
    if (filtriBase.commerciale) p.set("commerciale", filtriBase.commerciale);
    if (filtriBase.settore) p.set("settore", filtriBase.settore);
    api.get(`/dashboard/dealers-top?${p.toString()}`)
      .then((r) => {
        const lista = r.data.dealers || [];
        setNomi((n) => ({ ...n, ...Object.fromEntries(lista.map((d) => [d.codice, d.ragione_sociale])) }));
        onChange(lista.map((d) => d.codice).join(","));
      })
      .catch(() => {});
    setApri(false);
  };

  return (
    <div className="col-span-2 flex flex-wrap items-center gap-2"
      data-testid={`filtro-${tid}-multi`}>
      <div className="relative w-full sm:w-auto">
        <input className={`${cls} sm:w-[165px] ${escludi ? "border-red-300 text-red-700" : ""}`}
          data-testid={`filter-${tid}`} value={testo}
          placeholder={codici.length
            ? `${codici.length} convenzionati ${escludi ? "esclusi" : "selezionati"}`
            : `${escludi ? "Escludi convenzionati…" : "Cerca convenzionato…"}`}
          onChange={(e) => setTesto(e.target.value)} onFocus={() => setApri(false)} />
        {q && suggeriti.length > 0 && (
          <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-slate-200
            bg-white shadow-lg" data-testid={`filter-${tid}-suggeriti`}>
            {suggeriti.map((d) => (
              <button key={d.codice} type="button" onClick={() => aggiungi(d.codice)}
                data-testid={`filter-${tid}-opt-${d.codice}`}
                className="w-full text-left px-3 py-2 text-sm hover:bg-sky-50 transition-colors duration-150">
                <span className="font-medium">{d.ragione_sociale}</span>
                {d.pratiche_caricate !== undefined && (
                  <span className="text-xs text-slate-400 ml-2">{d.pratiche_caricate} pratiche</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="relative">
        <button type="button" onClick={() => setApri((v) => !v)} data-testid={`btn-top-${tid}`}
          className={`${cls} font-semibold ${escludi
            ? "text-red-700 border-red-300 hover:border-red-500"
            : "text-sky-700 border-sky-300 hover:border-sky-500"}`}>
          {escludi ? "Escludi top ▾" : "Top ▾"}
        </button>
        {apri && (
          <div className="absolute z-30 mt-1 w-64 rounded-md border border-slate-200 bg-white shadow-lg p-3 space-y-2"
            data-testid="menu-top-dealer">
            <select className={`${cls} w-full`} value={top.n} data-testid="top-n"
              onChange={(e) => setTop({ ...top, n: Number(e.target.value) })}>
              {[5, 10, 20, 50].map((n) => <option key={n} value={n}>Top {n}</option>)}
            </select>
            <select className={`${cls} w-full`} value={top.anno} data-testid="top-anno"
              onChange={(e) => setTop({ ...top, anno: Number(e.target.value) })}>
              {[ANNO_CORRENTE, ANNO_CORRENTE - 1, ANNO_CORRENTE - 2].map((a) => (
                <option key={a} value={a}>Anno {a}</option>
              ))}
            </select>
            <select className={`${cls} w-full`} value={top.metrica} data-testid="top-metrica"
              onChange={(e) => setTop({ ...top, metrica: e.target.value })}>
              <option value="importo_caricato">Importo caricato</option>
              <option value="importo_liquidato">Importo liquidato</option>
              <option value="pratiche_caricate">Pezzi caricati</option>
              <option value="pratiche_liquidate">Pezzi liquidati</option>
            </select>
            <button type="button" onClick={caricaTop} data-testid="btn-applica-top"
              className="w-full rounded-md bg-sky-500 px-3 py-2 text-sm font-semibold text-white
                hover:bg-sky-600 transition-colors duration-200">
              Applica selezione
            </button>
          </div>
        )}
      </div>

      {codici.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 w-full sm:max-w-[560px]"
          data-testid={`filter-${tid}-chips`}>
          {codici.map((c) => (
            <span key={c} data-testid={`chip-${tid}-${c}`}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium
                ${escludi ? "bg-red-50 border-red-300 text-red-700" : "bg-slate-100 border-slate-300"}`}>
              {nome(c).length > 24 ? `${nome(c).slice(0, 24)}…` : nome(c)}
              <button type="button" onClick={() => rimuovi(c)} data-testid={`chip-remove-${tid}-${c}`}
                className="text-slate-400 hover:text-red-600 transition-colors duration-150">×</button>
            </span>
          ))}
          <button type="button" onClick={() => onChange("")} data-testid={`chip-clear-all-${tid}`}
            className="text-xs text-sky-700 hover:underline ml-1">svuota</button>
        </div>
      )}
    </div>
  );
}

export function AzzeraFiltri({ onClick, testId = "btn-azzera-filtri" }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className="mb-6 inline-flex items-center gap-2 rounded-md bg-sky-500 px-4 py-2
        text-sm font-semibold text-white hover:bg-sky-600 transition-colors duration-200">
      Azzera filtri
    </button>
  );
}

/** Etichetta ufficiale del prodotto: "21 · AUTO USATA". */
export const prodottoLabel = (meta, codice) => {
  if (codice === null || codice === undefined || codice === "") return "—";
  const p = (meta?.prodotti || []).find((x) => String(x.codice) === String(codice));
  return p ? p.label : String(codice);
};

export const inputCls = cls;
